import { Theme } from "@mui/material";
import { TooltipComponentOption } from "echarts";

import { formatPercentValue } from "@/Utils/formatters";

import { ExtendedBarSeriesOption } from "../hooks/useSalesCompositionChartOptions";

const withLeftBorder = (color: string, children: string) => {
  return `
    <div style='border-left: 4px solid ${color}; padding: 16px; border-radius: 4px 0 0 4px;'>
      ${children}
    </div>`;
};

const renderChildren = (
  data: ExtendedBarSeriesOption["children"],
  dataIndex: number,
  formatter: (value: number) => string
): string => {
  if (!data) return "";
  const itemsPerColumn = 6;
  const numColumns = Math.ceil(data.length / itemsPerColumn);

  // Create column arrays
  const columns = Array.from({ length: numColumns }, (_, colIdx) => {
    return data.slice(colIdx * itemsPerColumn, colIdx * itemsPerColumn + itemsPerColumn);
  });

  // Render a single outer table with one row, multiple columns
  const columnHtml = columns
    .map(
      (column) => `
        <td style="vertical-align: top; padding-right: 24px;">
          <table>
            ${column
              .map(
                (d) => `
                <tr>
                  <td style="padding: 2px 12px 2px 0; white-space: nowrap; font-size: 12px; font-weight: 600;">${d.displayName}:</td>
                  <td style="text-align: right; white-space: nowrap; font-size: 12px;">${formatter(d.data[dataIndex] ?? 0)}</td>
                </tr>
              `
              )
              .join("")}
          </table>
        </td>
      `
    )
    .join("");

  return `<table><tr>${columnHtml}</tr></table>`;
};

const renderTooltipContent = (
  name: string,
  label: string,
  value: number,
  total: number,
  formatter: (value: number, showUnitsString?: boolean) => string
) => {
  return `
    <div style='display: flex; flex-direction: column; gap: 8px;'>
      <span style="font-size: 16px; font-weight: 600;">${name}</span>
      <div style='display: grid; grid-template-columns: 1fr 1fr; grid-column-gap: 8px; grid-row-gap: 1px;'>
        <span style="font-weight: 600;">${label}:</span>
        <span>${formatter(value, true)}</span>
        <span style="font-weight: 600;">Total Shown:</span>
        <span>${formatter(total, true)}</span>
        <span style="font-weight: 600;">Contribution:</span>
        <span>${total !== 0 ? formatPercentValue((value / total) * 100) : "0%"}</span>
      </div>
    </div>
  `;
};

export const getTooltip = (
  totalValues: number[],
  seriesData: ExtendedBarSeriesOption[],
  formatter: (value: number) => string,
  theme: Theme
): TooltipComponentOption => ({
  trigger: "item",
  formatter: (params: any) => {
    const total = totalValues[params.dataIndex] ?? 0;
    const series = seriesData.find((series) => series.name === params.seriesName);
    if (series?.children) {
      return `
        ${withLeftBorder(
          params.color,
          `
        <div style="padding-bottom: 0; margin-bottom: 0;">  
          ${renderTooltipContent(
            params.displayName || params.name,
            series.name || params.seriesName,
            params.value,
            total,
            formatter
          )}
          <div style="border-top: 1px solid ${theme.palette.divider}; margin: 16px 0 0 0; padding-top: 4px;">
            ${renderChildren(series.children, params.dataIndex, formatter)}
          </div>
        </div>
        `
        )}
      `;
    }
    return withLeftBorder(
      params.color,
      renderTooltipContent(
        params.displayName || params.name,
        params.seriesName,
        params.value,
        total,
        formatter
      )
    );
  },
});
